import { apiFetch, withToken } from "./http";
// GSPR session API istemcisi.
// Backend: go/internal/api/handler/gspr.go

const API_BASE = "/api/v1/gspr/sessions";

export type IntendedUseMode = "medical" | "aesthetic";
export type ContactMode = "direct" | "none";
// Biyouyumluluk alt sorusu — EN ISO 10993-1:2025 madde 6.4.4 (nitelik) ve 6.4.2 (süre).
// Değerler go/internal/domain/models/product_profile.go ile birebir aynı olmalı.
export type ContactNature = "intact_skin" | "mucosal" | "breached_or_internal" | "circulating_blood";
export type ContactDuration = "limited" | "prolonged" | "long_term";
export type SterilityMode = "sterile" | "pre_sterilization" | "non_sterile";
export type SterilizationMethod = "eto" | "radiation" | "steam" | "plasma" | "aseptic";
export type BiologicalOrigin = "none" | "animal_tissue" | "human_tissue" | "other_biological" | "drug";
export type ReuseMode = "single_use" | "reusable" | "n/a";
export type DeviceClass = "I" | "IIa" | "IIb" | "III";
// V02 ile gelen tipler — bkz. docs/GSPR Soru Listesi-V02.docx
export type SubstanceMode = "medicinal" | "absorbed" | "none";
export type IFUFormat = "paper" | "eifu_only" | "hybrid";
export type SharpMicrobialMode = "sharp_and_microbial" | "sharp_only" | "microbial_only" | "none";
export type CMRMode = "cmr" | "endocrine" | "both" | "none";

/**
 * ProductProfile — GSPR wizard'ın topladığı cevaplar.
 *
 * Alan adları go/internal/domain/models/product_profile.go ile birebir aynıdır.
 * Yorumlardaki soru numaraları "GSPR Soru Listesi-V02.docx"e; SPR kodları da
 * kural tablosuna (go/internal/usecase/qa_flow/rules_v02.go) atıf yapar.
 */
export type ProductProfile = {
  intended_use_text?: string;
  intended_use?: IntendedUseMode;              // Soru-1  → SPR 9, 23.4(x)
  class?: DeviceClass;
  rule?: number;
  regulation?: "MDR";                          // sistem yalnızca MDR (2017/745) uyumlu
  contact?: ContactMode;                       // Soru-2  → 10.1(a,b,h), 11.1(b)
  contact_natures?: ContactNature[];           // Soru-2 ALT → SPR 10 standart atfı (çok seçim)
  // "" = temizlendi. undefined gönderilemez: JSON.stringify onu düşürür ve
  // sunucudaki eski cevap kalırdı.
  contact_duration?: ContactDuration | "";     // Soru-2 ALT → SPR 10 standart atfı (tek seçim)
  sterility?: SterilityMode;                   // Soru-3  → 11.x, 23.2(l), 23.3, 23.4(l,m,i)
  sterilization_method?: SterilizationMethod;  // Soru-3b → standart atfı (ISO 11135/11137/17665…)
  dual_sterile_non_sterile?: boolean;          // Soru-4  → 11.8
  active?: boolean;                            // Soru-5  → 18.1-18.8, 14.2(d), 20.4
  active_implantable?: boolean;                // Soru-6  → 19.1-19.4, 23.2(s)
  software?: boolean;                          // Soru-7  → 17.1-17.4, 23.4(f), 23.4(ab)
  emits_radiation?: boolean;                   // Soru-8  → 16.1-16.3, 23.4(r)
  ionizing_radiation?: boolean;                // Soru-9  → 16.4 (yalnızca Soru-8 evet ise)
  substance_mode?: SubstanceMode;              // Soru-10 → 12.1 | 12.2 + 23.2(r) + 23.4(t)
  implantable?: boolean;                       // Soru-11 → 10.1(c), 23.4(u,d,aa)
  delivers_energy_or_substance?: boolean;      // Soru-12 → 21.1-21.3
  measuring_function?: boolean;                // Soru-13 → 14.6, 15.1, 15.2
  // Soru-14 — çoklu seçim
  combination_use?: boolean;                   //         → 14.1, 14.5, 23.4(q)
  environmental_factors?: boolean;             //         → 14.2(a,b,c,e,f,g), 10.5
  flammable_env?: boolean;                     //         → 14.3
  requires_maintenance?: boolean;              // Soru-15 → 14.4, 23.4(k)
  // Soru-16 — çoklu seçim. NOT: SPR 20.4 buraya DEĞİL Soru-5'e bağlıdır.
  moving_parts?: boolean;                      //         → 20.1, 20.5
  vibration_noise?: boolean;                   //         → 20.2, 20.3
  dangerous_temp?: boolean;                    //         → 20.6
  lay_person_use?: boolean;                    // Soru-17 → 22.1-22.3, 23.4(w)
  has_ifu?: boolean;                           // Soru-18 → 14.7, 23.1(d), 23.4(*)
  ifu_format?: IFUFormat;                      // Soru-19 → 23.1(f)
  reuse_mode?: ReuseMode;                      // Soru-20 → 11.2, 23.2(n), 23.4(n,p)
  reprocessed_by_manufacturer_only?: boolean;  // Soru-21 → 23.4(o)
  multipack_single_ifu?: boolean;              // Soru-22 → 23.1(e)
  manufacturer_outside_eu?: boolean;           // Soru-23 → 23.2(d)
  has_nanomaterial?: boolean;                  // Soru-24 → 10.6
  sharp_microbial?: SharpMicrobialMode;        // Soru-25 → 11.1(a), 11.1(d)
  refurbished_single_use?: boolean;            // Soru-26 → 23.2(o)
  biological_origin?: BiologicalOrigin;        // Soru-27 → 13.1-13.3, 23.2(e)
  contaminants_controlled?: boolean;           // Soru-28 → 10.1(d), 10.2
  substance_interaction_verified?: boolean;    // Soru-29 → 10.3
  administers_medicinal_products?: boolean;    // Soru-29 ALT → 10.3 gerekçesi
  cmr_substances?: CMRMode;                    // Soru-30 → 10.4.1/10.4.2/10.4.4/10.4.5, 23.2(f)
  contains_phthalates?: boolean;               // Soru-30 ALT → 10.4.3
  biophysical_modeling?: boolean;              // Soru-31 → 10.1(e)
  mechanical_stress?: boolean;                 // Soru-32 → 10.1(f)
  surface_critical?: boolean;                  // Soru-33 → 10.1(g)
  special_storage_transport?: boolean;         // Soru-34 → 23.2(k)
  custom_made?: boolean;                       // Soru-35 → 23.2(p)
  special_training_facility?: boolean;         // Soru-36 → 23.4(j)
  // Sınıflandırma girdisi (MDR Ek-VIII Kural 20) — GSPR sorusu DEĞİL,
  // hiçbir SPR maddesine bağlı değildir.
  inhalation_route?: boolean;
  // Cover page (wizard intro'da girilir)
  product_name?: string;
  risk_class_display?: string;
  gspr_doc_code?: string;
  customer_id?: string;
  customer_name?: string;
  customer_logo_data_url?: string;
};

export type Session = {
  id: string;
  org_id?: number;
  profile: ProductProfile;
  created_at: string;
  updated_at: string;
};

export type Applicability = "applicable" | "not_applicable";

export type SubClause = {
  letter: string;
  text_en: string;
  text_tr: string;
  applicability: Applicability;
  justification?: string;
  source_rule?: string;
  /** MDR Ek-I bent metni — ana maddedeki gibi yönetmelikten (backend doldurur). */
  requirement_text?: string;
  /** Bendin KENDİ uyum ifadesi (LLM) — ana madde ile aynı boru hattından. */
  generated_text?: string;
};

export type SPRDecision = {
  spr: string;
  title: string;
  chapter: number;
  applicability: Applicability;
  justification: string;
  required_stds: string[];
  hazard_refs: string[];
  source_rule: string;
  sub_clauses?: SubClause[];
  generated_text?: string;
  generated_text_tr?: string;
  requirement_text?: string;
  requirement_text_tr?: string;
};

export type Match = {
  Rule: number;
  Class: string;
  Reason: string;
};

export type SPRMapResponse = {
  session_id: string;
  profile: ProductProfile;
  classification: Match;
  all_matches: Match[];
  decisions: SPRDecision[];
  summary: { total: number; applicable: number; not_applicable: number };
  generation_warning?: string;
};

export type SessionListResponse = {
  count: number;
  limit: number;
  sessions: Session[];
};

export async function listSessions(limit = 20): Promise<SessionListResponse> {
  const res = await apiFetch(`${API_BASE}?limit=${limit}`);
  if (!res.ok) throw new Error(`listSessions: HTTP ${res.status}`);
  return res.json();
}

export async function deleteSession(id: string): Promise<void> {
  const res = await apiFetch(`${API_BASE}/${id}`, { method: "DELETE" });
  if (!res.ok && res.status !== 404) throw new Error(`deleteSession: HTTP ${res.status}`);
}

export async function getSession(id: string): Promise<Session> {
  const res = await apiFetch(`${API_BASE}/${id}`);
  if (!res.ok) throw new Error(`getSession: HTTP ${res.status}`);
  return res.json();
}

export async function createSession(orgId?: number): Promise<Session> {
  const res = await apiFetch(`${API_BASE}/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(orgId ? { org_id: orgId } : {}),
  });
  if (!res.ok) throw new Error(`createSession: HTTP ${res.status}`);
  return res.json();
}

export async function patchSession(id: string, patch: Partial<ProductProfile>): Promise<Session> {
  const res = await apiFetch(`${API_BASE}/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`patchSession: HTTP ${res.status}`);
  return res.json();
}

export async function getSPRMap(id: string): Promise<SPRMapResponse> {
  const res = await apiFetch(`${API_BASE}/${id}/spr-map`);
  if (!res.ok) throw new Error(`getSPRMap: HTTP ${res.status}`);
  return res.json();
}

export type FinalizeStatus = "idle" | "running" | "done" | "error";
export type FinalizeResult = {
  status?: FinalizeStatus; // async: "running" | "done"; senkron: "done"; yoksa (eski) → done kabul
  document_id?: number;
  credit_balance?: number;
  already?: boolean;
  error?: string;
};

// finalizeSession — "Dokümanı Oluştur": 1 kredi harcayıp immutable doküman üretir.
// Gap analiziyle üretim uzadığı için backend işi ARKA PLANA alır ve
// {status:"running"} (202) döner; bu durumda getFinalizeStatus ile yoklanır.
// Zaten üretilmişse {status:"done", document_id} döner. 402 → yetersiz kredi.
export async function finalizeSession(id: string): Promise<FinalizeResult> {
  const res = await apiFetch(`${API_BASE}/${id}/finalize`, { method: "POST" });
  if (res.status === 402) throw new Error("insufficient_credit");
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

// getFinalizeStatus — arka plan üretim durumunu yoklar.
// status: "done" (document_id ile) | "running" | "error" (error ile) | "idle".
export async function getFinalizeStatus(id: string): Promise<FinalizeResult> {
  const res = await apiFetch(`${API_BASE}/${id}/finalize/status`);
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

export type LLMProvider = "openai" | "gemini";

export function documentDownloadURL(id: string, provider?: LLMProvider, chapters?: number[], skipLLM?: boolean): string {
  const base = `${API_BASE}/${id}/document.docx`;
  const params = new URLSearchParams();
  if (provider) params.set("provider", provider);
  if (chapters && chapters.length > 0) params.set("chapter", chapters.join(","));
  if (skipLLM) params.set("llm", "false");
  const qs = params.toString();
  // NOT: tüm query parametreleri burada eklenir; dönen URL'e ELLE ?... eklenmez
  // (withToken access_token'ı zaten ekledi → ikinci '?' token'ı bozar).
  return withToken(qs ? `${base}?${qs}` : base);
}

export type CERSection = {
  id: string;
  title: string;
  title_tr: string;
  description: string;
  applicable: boolean;
};

export type CERReport = {
  cep_parts: CERSection[];
  cer_parts: CERSection[];
  generated_at: string;
};

export async function getCER(id: string): Promise<CERReport> {
  const res = await apiFetch(`${API_BASE}/${id}/cer`);
  if (!res.ok) throw new Error(`getCER: HTTP ${res.status}`);
  return res.json();
}

export type Severity = "major" | "minor" | "observation";

export type Finding = {
  spr: string;
  severity: Severity;
  code: string;
  message: string;
  message_tr?: string;
  excerpt?: string;
};

export type AuditReport = {
  risk_score: number;
  cell_count: number;
  llm_used: boolean;
  summary: Record<Severity, number>;
  findings: Finding[] | null;
};

export async function getAudit(id: string, opts?: { skipLLM?: boolean; provider?: LLMProvider }): Promise<AuditReport> {
  const params = new URLSearchParams();
  if (opts?.skipLLM) params.set("llm", "false");
  if (opts?.provider) params.set("provider", opts.provider);
  const qs = params.toString();
  const res = await apiFetch(`${API_BASE}/${id}/audit${qs ? `?${qs}` : ""}`);
  if (!res.ok) throw new Error(`getAudit: HTTP ${res.status}`);
  return res.json();
}

export type FilledCellSource = {
  id: string;
  regulation?: string;
  article?: string;
  annex?: string;
  heading?: string;
  content?: string;
  lang?: string;
  standard?: string;
  version?: string;
  category?: string;
};

export type FilledCell = {
  spr: string;
  title: string;
  title_tr?: string;
  chapter: number;
  applicability: Applicability;
  standards_applied: string[];
  requirement_text?: string;
  requirement_text_tr?: string;
  generated_text: string;
  generated_text_tr: string;
  tf_references?: string[];
  complied_with: string;
  source_rule: string;
  sources?: FilledCellSource[];
};

export function auditMarkdownURL(id: string, opts?: { skipLLM?: boolean }): string {
  const q = opts?.skipLLM ? "?format=md&llm=false" : "?format=md";
  return withToken(`${API_BASE}/${id}/audit${q}`);
}

export function auditHTMLURL(id: string, opts?: { skipLLM?: boolean }): string {
  const q = opts?.skipLLM ? "?format=html&llm=false" : "?format=html";
  return withToken(`${API_BASE}/${id}/audit${q}`);
}

export async function regenerateCell(id: string, spr: string): Promise<FilledCell> {
  const res = await apiFetch(`${API_BASE}/${id}/cells/${encodeURIComponent(spr)}/regenerate`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`regenerateCell ${spr}: HTTP ${res.status}`);
  return res.json();
}
